import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClasses = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-energy/50 bg-navy-card/90 text-cream shadow-lg transition-colors duration-150 ease-out hover:bg-navy-light focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50';
  return <section className="py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-92" data-mp="1wd6dkvdyuc1q">
      <div className="mx-auto max-w-6xl px-4" data-id="element-93" data-mp="3vpijjj8fevgc">
        <div className="text-center" data-id="element-94" data-mp="3vo7gjlmt1rqa">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-energy" data-id="element-95" data-mp="3qq5s0l6m107f">Guarda dentro</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-extrabold uppercase leading-tight tracking-tight text-cream sm:text-4xl" data-id="element-96" data-mp="1p08a79ckdat1">
            Scopri cosa c&apos;è dentro…
          </h2>
          <span className="mx-auto mt-4 block h-1 w-14 rounded-full bg-gradient-to-r from-energy to-aqua" aria-hidden="true" data-id="element-97" data-mp="1f3loyjgbshqh" />
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-haze" data-id="element-98" data-mp="1k1wy0rysqo7n">
            Pagine visive con infografiche, schemi chiari, formule spiegate passo passo, punti chiave, errori comuni degli
            studenti e attività pronte per la classe. Ecco alcune pagine reali del Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carosello" aria-label="Pagine di esempio" data-id="element-99" data-mp="1ysw2prwigsly">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] pb-2 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-100" data-mp="nbmllc0uucnt">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border bg-navy-card shadow-lg transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-energy/70 opacity-100' : 'border-aqua/20 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} di ${total}`} data-id="element-101" data-mp="eudx5do4rk5q" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-102" data-mp="138t7zdqc5pl" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${arrowClasses} left-0 sm:-left-2`} aria-label="Pagina precedente" data-id="element-103" data-mp="aubscvn3mam8">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-104" data-mp="3ehlx8j2az114" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${arrowClasses} right-0 sm:-right-2`} aria-label="Pagina successiva" data-id="element-105" data-mp="39isjcpj3t80f">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-106" data-mp="2csp15hazttat" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-107" data-mp="2gtsi674s1vdm">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-energy ${index === active ? 'w-6 bg-energy' : 'w-2 bg-aqua/30 hover:bg-aqua/60'}`} aria-label={`Vai alla pagina ${index + 1}`} aria-current={index === active} data-id="element-108" data-mp="2lr0xlzyjjlb6" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}